import React, { createContext, useState, useEffect, useContext } from 'react';
import { loginUser, registerUser, getCurrentUser, logoutUser, isAuthenticated, setAuthToken } from '../services/auth';

// Create Auth Context
const AuthContext = createContext();

// Create Auth Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Initialize auth state - check for existing token and get user data
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      
      // Check if user is authenticated (has token)
      if (isAuthenticated()) {
        // Set auth token in axios headers
        const token = localStorage.getItem('token');
        setAuthToken(token);
        
        try {
          // Get current user data
          const userData = await getCurrentUser();
          setCurrentUser(userData);
          setIsLoggedIn(true);
        } catch (err) {
          console.error('Failed to load user:', err);
          // Clear invalid token
          logoutUser();
          setIsLoggedIn(false);
        }
      }
      
      setLoading(false);
    };
    
    initAuth();
  }, []);

  // Login handler
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = {"email": email, "password": password}
      const result = await loginUser(data);
      
      try {
        // Get current user data
        const userData = await getCurrentUser();
        setCurrentUser(userData);
        setIsLoggedIn(true);
      } catch (err) {
        console.error('Failed to load user:', err);
        // Clear invalid token
        logoutUser();
        setIsLoggedIn(false);
      }
      return result;
    } catch (err) {
      setError(err.message || 'Failed to login');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register handler
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await registerUser(userData);
      return result;
    } catch (err) {
      setError(err.message || 'Failed to register');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout handler
  const logout = () => {
    logoutUser();
    setCurrentUser(null);
    setIsLoggedIn(false);
  };

  // Update user info in context
  const updateUserInfo = (userData) => {
    setCurrentUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Context value
  const value = {
    currentUser,
    isLoggedIn,
    loading,
    error,
    login,
    register,
    logout,
    updateUserInfo,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;